import type { DynamicFieldType, FieldValue } from '../api/dynamicFields'

/**
 * R3-12 (REQ-041-001, REQ-043-003): regole pure dei campi della pratica, senza React ne' rete, per poterle provare.
 * Valore predefinito (da testo del modulo di configurazione a valore JSON e ritorno), valore iniziale in una nuova
 * pratica e controllo "obbligatorio" all'uscita dal campo.
 */

export type EditValue = string | boolean | string[]

/** Tipi che ammettono un valore predefinito: i calcolati, i file e le firme non si precompilano. */
export function supportsDefault(type: DynamicFieldType): boolean {
  return type !== 'Calcolato' && type !== 'File' && type !== 'Firma'
}

/** Testo digitato dall'amministratore -> valore JSON del predefinito (`null` = nessun predefinito). */
export function textToDefault(type: DynamicFieldType, text: string): FieldValue {
  const t = text.trim()
  if (t === '' || !supportsDefault(type)) return null
  switch (type) {
    case 'Casella':
      return t === 'true' ? true : null
    case 'Numero':
    case 'Importo':
    case 'Percentuale': {
      const n = Number(t.replace(',', '.'))
      return Number.isFinite(n) ? n : t
    }
    case 'ElencoMultiplo':
      return t.split(';').map(x => x.trim()).filter(x => x !== '')
    default:
      return t
  }
}

/** Valore predefinito salvato -> testo da mostrare nel modulo di configurazione. */
export function defaultToText(type: DynamicFieldType, value: FieldValue | undefined): string {
  if (value === null || value === undefined) return ''
  if (type === 'Casella') return value === true ? 'true' : ''
  if (Array.isArray(value)) return value.join('; ')
  return String(value)
}

/** Valore con cui parte il campo in una nuova pratica: il predefinito se c'e', altrimenti vuoto. */
export function initialEditValue(type: DynamicFieldType, defaultValue: FieldValue | undefined): EditValue {
  if (type === 'Casella') return defaultValue === true
  if (type === 'ElencoMultiplo') return Array.isArray(defaultValue) ? [...defaultValue] : []
  if (defaultValue === null || defaultValue === undefined || typeof defaultValue === 'object') return ''
  return String(defaultValue)
}

function isEmptyEdit(v: EditValue | undefined): boolean {
  if (v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  return false
}

/** Messaggio se un campo visibile e obbligatorio e' vuoto; `null` altrimenti. Una casella obbligatoria deve essere spuntata. */
export function requiredMessage(
  field: { type: DynamicFieldType; required: boolean; label: string },
  value: EditValue | undefined,
  visible: boolean,
): string | null {
  if (!field.required || !visible || field.type === 'Calcolato') return null
  if (field.type === 'Casella') return value === true ? null : `${field.label}: campo obbligatorio.`
  return isEmptyEdit(value) ? `${field.label}: campo obbligatorio.` : null
}
